// (B) Plain(커스텀) Dropdown 백엔드 — antd Dropdown 대체. 포지셔닝/포털/클릭아웃/Esc 는 Radix Popover 재사용
//   (Radix DropdownMenu 는 selectable/activeKey/popupRender 임의콘텐츠와 충돌 → Popover+커스텀 리스트).
//   antd Dropdown v5 DOM 을 이중클래스로 미러링(각 요소에 `.om-dropdown-*` + `.om-dropdown-*` 동시 부여):
//     트리거(children): .om-dropdown-trigger + .om-dropdown (+ 열림 시 .om-dropdown-open + .om-dropdown-open)
//     팝업: .om-dropdown + .om-dropdown-overlay > .om-dropdown-menu + .om-dropdown-menu > 항목
//     항목: .om-dropdown-menu-item(+-selected/-active/-disabled/-danger) > .om-dropdown-menu-title-content
//     구분선 .om-dropdown-menu-item-divider · 그룹 .om-dropdown-menu-item-group(>-group-title/-group-list)
//     서브메뉴 .om-dropdown-menu-submenu(>-submenu-title) → hover flyout(중첩 Popover)
//   → 기존 소비처/전역(officemail.scss .contextMenu)/ninegqlUI SCSS·JS DOM 쿼리 전량 무변경 매칭. rename 은 batch9 유예.
//
// ⚠ JS 결합(§7-A) 무중단 근거:
//   - pages/main·EmailContainer blurHandler 가 `.om-dropdown-open` 트리거를 querySelectorAll→click() 로 강제 닫음
//     → 트리거에 `.om-dropdown-open` 미러링 + click 트리거는 열림 상태에서 클릭 시 토글-닫힘(Radix Popover.Trigger),
//       contextMenu 트리거는 좌클릭이 outside-dismiss 로 닫힘 → 무중단.
//   - CustomTimeSelector·WorkHoursPanel·CustomDatePicker 가 `.om-dropdown-menu-item(-selected)` add/remove +
//     `closest('.om-dropdown-menu-item')` + scrollIntoView → menu.items 를 `.om-dropdown-menu`>`.om-dropdown-menu-item`
//       구조로 방출 + selectedKeys 를 `-selected` 로 반영 → 무중단.
// ⚠ contextMenu 트리거: antd 는 커서 위치에 팝업 → fixed 0-size 가상 Anchor 를 커서 좌표에 두고 Popover 를 앵커링.
// ⚠ hover 트리거: OmwDropdownMenu 실사용 0(모두 click/contextMenu/[]) → 미구현(EmailForm 의 hover 는 OmwTooltip 소관).
// 시각 base 는 styles/omwDropdownMenu.css(:where 0-특이도 → 소비처 .contextMenu SCSS 항상 우선).
import * as PopoverPrimitive from '@radix-ui/react-popover'
import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import type { OmwDropdownMenuConfig, OmwDropdownMenuProps } from '../api/OmwDropdownMenu'
import type { OmwMenuClickInfo, OmwMenuItem } from '../api/OmwMenu'
import { cx } from './cx'
import { useRovingKeydown } from './a11y/useRovingKeydown'
import '../styles/omwDropdownMenu.css'

type Side = 'top' | 'right' | 'bottom' | 'left'
type Align = 'start' | 'center' | 'end'

// antd placement → Radix side/align. antd 기본은 bottomLeft.
const PLACEMENT_MAP: Record<string, { side: Side; align: Align }> = {
  top: { side: 'top', align: 'center' },
  topLeft: { side: 'top', align: 'start' },
  topRight: { side: 'top', align: 'end' },
  bottom: { side: 'bottom', align: 'center' },
  bottomLeft: { side: 'bottom', align: 'start' },
  bottomRight: { side: 'bottom', align: 'end' },
  left: { side: 'left', align: 'center' },
  right: { side: 'right', align: 'center' },
}

// PLACEMENT_MAP 은 Record<string, …> 라 index 접근이 `T | undefined` 다(noUncheckedIndexedAccess).
// antd 기본값(bottomLeft)을 non-undefined 상수로 분리해 미지의 placement 에도 안전한 fallback 을 준다.
const DEFAULT_PLACEMENT: { side: Side; align: Align } = { side: 'bottom', align: 'start' }

// 동시에 열린 드롭다운 조율 — 각 OmwDropdownMenu 는 독립 Popover Root 라 서로를 모른다. 새 드롭다운이
// 열릴 때 "자신의 조상이 아닌" 열린 드롭다운을 모두 닫아, 한 번에 하나의 메뉴 트리만 열리게 한다.
// (조상=중첩 상위 메뉴. 중첩 메뉴는 부모 팝업 안에 렌더되므로 공존을 허용한다.)
const openDropdowns = new Map<string, { close: () => void }>()

// 열린 om-dropdown 을 트리거 종류와 무관하게 전부 닫는다. blurHandler 처럼 DOM 트리거 `.click()`
// 으로 닫는 방식은 contextMenu(우클릭 전용 트리거, 좌클릭 무반응)·none(트리거 없음) 을 닫지 못하므로,
// 레지스트리의 close() 를 직접 호출해 모든 모드를 닫는다. close() 가 레지스트리를 변형(delete)하므로
// 스냅샷 후 순회한다.
export function closeAllOpenDropdowns(): void {
  for (const entry of [...openDropdowns.values()]) {
    entry.close()
  }
}

// 조상 드롭다운 id 체인. 팝업은 body 로 portal 되어 DOM 조상 판정이 불가하나, React Context 는 portal 을
// 통과하므로 중첩 드롭다운이 자신의 조상 체인을 정확히 상속받는다.
const DropdownAncestorContext = createContext<readonly string[]>([])

/** data-* 속성만 추려 DOM 으로 패스스루(항목 로케이팅용, antMenuItems.toAntItems 대응). */
function pickDataAttrs(item: OmwMenuItem): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(item)) {
    if (k.startsWith('data-')) out[k] = (item as Record<string, unknown>)[k]
  }
  return out
}

/** 로빙 대상(활성 leaf) 수집 — 그룹 자식까지, 서브메뉴는 제외(flyout 은 hover 전용). */
function collectLeaves(items: OmwMenuItem[]): OmwMenuItem[] {
  const out: OmwMenuItem[] = []
  for (const it of items) {
    if (it.type === 'divider') continue
    if (it.type === 'group') {
      if (it.children) out.push(...collectLeaves(it.children))
      continue
    }
    if (it.children) continue // 서브메뉴 타이틀은 로빙 제외
    if (!it.disabled) out.push(it)
  }
  return out
}

interface SubMenuProps {
  item: OmwMenuItem
  renderItems: (items: OmwMenuItem[]) => ReactNode
}

/** 서브메뉴(children, type 없음) — hover 로 열리는 중첩 Popover flyout. popupClassName/popupOffset 반영. */
function PlainSubMenu({ item, renderItems }: SubMenuProps) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = () => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openNow = () => {
    clearTimer()
    setOpen(true)
  }
  const closeSoon = () => {
    clearTimer()
    closeTimer.current = setTimeout(() => setOpen(false), 100)
  }

  // 언마운트 시 대기 중인 close 타이머 정리(CLAUDE.md: cleanup 없는 setTimeout 금지)
  useEffect(() => () => clearTimer(), [])

  const offset = item.popupOffset
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <li
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        className={cx(
          'om-dropdown-menu-submenu',
          open && 'om-dropdown-menu-submenu-open',
          item.className,
        )}
        style={item.style}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
      >
        <PopoverPrimitive.Anchor asChild>
          <div className="om-dropdown-menu-submenu-title" title={item.title}>
            {item.icon != null && <span className="om-dropdown-menu-item-icon">{item.icon}</span>}
            <span className="om-dropdown-menu-title-content">{item.label}</span>
            <span className="om-dropdown-menu-submenu-arrow" aria-hidden="true" />
          </div>
        </PopoverPrimitive.Anchor>
      </li>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cx(
            'om-dropdown',
            'om-dropdown-menu-submenu-popup',
            'om-dropdown-overlay',
            'om-dropdown-submenu-popup',
            item.popupClassName,
          )}
          side="right"
          align="start"
          sideOffset={offset ? offset[0] : 0}
          alignOffset={offset ? offset[1] : -4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <ul
            className={cx(
              'om-dropdown-menu',
              'om-dropdown-menu-sub',
              'om-dropdown-menu-vertical',
              'om-dropdown-menu',
            )}
            role="menu"
          >
            {item.children != null && renderItems(item.children)}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

export function PlainDropdownMenu({
  menu,
  popupRender,
  trigger,
  open,
  onOpenChange,
  placement = 'bottomLeft',
  disabled,
  overlayClassName,
  overlayStyle,
  align,
  getPopupContainer,
  className,
  children,
}: OmwDropdownMenuProps) {
  const [innerOpen, setInnerOpen] = useState(false)
  const [ctxPos, setCtxPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const menuUlRef = useRef<HTMLUListElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const baseId = useId()
  // 마지막 activeKey 변경이 키보드 로빙인지 표시 → hover 로 바뀐 활성은 scrollIntoView 안 함
  //   (긴 목록에서 부분가시 항목 hover 시 목록이 점프하는 회귀 방지).
  const keyboardNavRef = useRef(false)

  // 항목 key → aria-activedescendant 용 안정 DOM id(비-id-safe 문자 치환).
  const domIdFor = useCallback(
    (key: string) => `${baseId}-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
    [baseId],
  )

  const isControlled = open !== undefined
  const isOpen = isControlled ? !!open : innerOpen

  // 이 드롭다운의 조상 id 체인(context) 과, 자식 팝업에 넘길 체인(+자신).
  const ancestorIds = useContext(DropdownAncestorContext)
  const childAncestorIds = useMemo(() => [...ancestorIds, baseId], [ancestorIds, baseId])
  // 다른 인스턴스가 호출할 "자신 닫기" — 최신 handleOpenChange 를 ref 로 참조(아래에서 갱신).
  const closeSelfRef = useRef<() => void>(() => {})

  const mode: 'click' | 'contextMenu' | 'none' = useMemo(() => {
    if (trigger == null) return 'click' // antd 기본은 hover 이나 OmwDropdownMenu 실사용 0 → click 로 수렴
    if (trigger.length === 0) return 'none' // 트리거 없음(controlled open 전용)
    if (trigger.includes('contextMenu')) return 'contextMenu'
    return 'click'
  }, [trigger])

  const setOpen = useCallback(
    (next: boolean) => {
      if (disabled && next) return
      if (!isControlled) setInnerOpen(next)
      onOpenChange?.(next)
    },
    [disabled, isControlled, onOpenChange],
  )

  // 선택형 leaf 클릭 처리 — onClick/menu.onClick + selectable(single/multiple) onSelect/onDeselect,
  //   multiple 이 아닐 때만 닫힘(MailTab multiple 필터는 유지).
  const commitLeaf = useCallback(
    (
      item: OmwMenuItem,
      domEvent: ReactMouseEvent<HTMLElement> | ReactKeyboardEvent<HTMLElement>,
    ) => {
      if (item.disabled) return
      const key = item.key ?? ''
      const info: OmwMenuClickInfo = { key, domEvent }
      item.onClick?.(info)
      menu?.onClick?.(info)
      const cfg: OmwDropdownMenuConfig | undefined = menu
      if (cfg?.selectable) {
        const keys = cfg.selectedKeys ?? []
        const was = keys.includes(key)
        if (cfg.multiple) {
          const next = was ? keys.filter((k) => k !== key) : [...keys, key]
          if (was) cfg.onDeselect?.({ key, selectedKeys: next })
          else cfg.onSelect?.({ key, selectedKeys: next })
        } else {
          cfg.onSelect?.({ key, selectedKeys: [key] })
        }
      }
      if (!(menu?.selectable && menu?.multiple)) setOpen(false)
    },
    [menu, setOpen],
  )

  const selectedKeys = menu?.selectedKeys
  const externalActiveKey = menu?.activeKey ?? null

  const renderItems = useCallback(
    (items: OmwMenuItem[]): ReactNode =>
      items.map((item, index) => {
        const key = item.key ?? `__idx_${index}`
        if (item.type === 'divider') {
          return <li key={key} role="separator" className="om-dropdown-menu-item-divider" />
        }
        if (item.type === 'group') {
          return (
            <li key={key} role="presentation" className="om-dropdown-menu-item-group">
              <div role="presentation" className="om-dropdown-menu-item-group-title">
                {item.label}
              </div>
              <ul role="group" className="om-dropdown-menu-item-group-list">
                {item.children != null && renderItems(item.children)}
              </ul>
            </li>
          )
        }
        if (item.children != null) {
          return <PlainSubMenu key={key} item={item} renderItems={renderItems} />
        }
        const isSelected = selectedKeys?.includes(item.key ?? '') ?? false
        const isActive =
          (externalActiveKey != null && externalActiveKey === item.key) ||
          (activeKey != null && activeKey === item.key)
        // selectable 메뉴: 다중=checkbox / 단일=radio 로 노출 + aria-checked 로 선택 상태 전달.
        const itemRole = menu?.selectable
          ? menu?.multiple
            ? 'menuitemcheckbox'
            : 'menuitemradio'
          : 'menuitem'
        return (
          <li
            key={key}
            id={item.key != null ? domIdFor(item.key) : undefined}
            role={itemRole}
            aria-disabled={item.disabled || undefined}
            aria-checked={menu?.selectable ? isSelected : undefined}
            title={item.title}
            style={item.style}
            className={cx(
              'om-dropdown-menu-item',
              isSelected && 'om-dropdown-menu-item-selected',
              isActive && 'om-dropdown-menu-item-active',
              item.disabled && 'om-dropdown-menu-item-disabled',
              item.danger && 'om-dropdown-menu-item-danger',
              item.className,
            )}
            onClick={(e) => commitLeaf(item, e)}
            onMouseEnter={(e) => {
              keyboardNavRef.current = false
              setActiveKey(item.key ?? null)
              item.onMouseEnter?.({ key: item.key ?? '', domEvent: e })
            }}
            onMouseLeave={(e) => item.onMouseLeave?.({ key: item.key ?? '', domEvent: e })}
            {...pickDataAttrs(item)}
          >
            {item.icon != null && <span className="om-dropdown-menu-item-icon">{item.icon}</span>}
            <span className="om-dropdown-menu-title-content">{item.label}</span>
          </li>
        )
      }),
    [selectedKeys, externalActiveKey, activeKey, commitLeaf, menu, domIdFor],
  )

  const flatLeaves = useMemo(() => (menu ? collectLeaves(menu.items) : []), [menu])

  const activeIndex = useMemo(
    () => flatLeaves.findIndex((it) => it.key === activeKey),
    [flatLeaves, activeKey],
  )
  const activeDescId = activeKey != null ? domIdFor(activeKey) : undefined

  // type-ahead 라벨 — title(문자열) 우선, 없으면 문자열 label.
  const getLeafLabel = useCallback(
    (i: number) => {
      const it = flatLeaves[i]
      if (!it) return ''
      if (typeof it.title === 'string') return it.title
      if (typeof it.label === 'string') return it.label
      return ''
    },
    [flatLeaves],
  )

  // Enter/Space activate 시 소비처 onClick 에 넘길 원본 keydown 이벤트 보관.
  const lastKeyEvent = useRef<ReactKeyboardEvent<HTMLElement> | null>(null)
  const { onKeyDown: rovingKeyDown } = useRovingKeydown({
    itemCount: flatLeaves.length,
    activeIndex,
    setActiveIndex: (i) => {
      keyboardNavRef.current = true
      setActiveKey(flatLeaves[i]?.key ?? null)
    },
    onActivate: (i) => {
      const it = flatLeaves[i]
      if (it && lastKeyEvent.current) commitLeaf(it, lastKeyEvent.current)
    },
    getLabel: getLeafLabel,
    orientation: 'vertical',
  })
  const onMenuKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLUListElement>) => {
      lastKeyEvent.current = e
      rovingKeyDown(e)
    },
    [rovingKeyDown],
  )

  // 활성 항목을 뷰포트 안으로 — 키보드 로빙일 때만(hover 로 바뀐 활성은 스크롤 점프 방지).
  useEffect(() => {
    if (activeDescId == null || !keyboardNavRef.current) return
    keyboardNavRef.current = false
    document.getElementById(activeDescId)?.scrollIntoView({ block: 'nearest' })
  }, [activeDescId])

  // menu.items → `.om-dropdown-menu` ul. popupRender 는 이 노드를 인자로 받아 임의 JSX 로 감쌀 수 있다.
  const menuNode: ReactNode =
    menu != null ? (
      <ul
        ref={menuUlRef}
        role="menu"
        tabIndex={menu.tabIndex ?? -1}
        aria-activedescendant={activeDescId}
        onKeyDown={onMenuKeyDown}
        className={cx(
          'om-dropdown-menu',
          'om-dropdown-menu-root',
          'om-dropdown-menu-vertical',
          'om-dropdown-menu',
        )}
      >
        {renderItems(menu.items)}
      </ul>
    ) : undefined

  const content: ReactNode = popupRender ? popupRender(menuNode) : menuNode

  const { side, align: rAlign } = PLACEMENT_MAP[placement] ?? DEFAULT_PLACEMENT
  // antd align.offset=[x,y]: x=교차축(alignOffset), y=주축 간격(sideOffset). 기본 간격 4.
  const sideOffset = align?.offset ? align.offset[1] : 4
  const alignOffset = align?.offset ? align.offset[0] : 0

  const triggerClass = cx(
    'om-dropdown-trigger',
    'om-dropdown',
    isOpen && 'om-dropdown-open',
    isOpen && 'om-dropdown-open',
    className,
  )

  const triggerChild: ReactElement = isValidElement(children) ? (
    (children as ReactElement)
  ) : (
    <span>{children}</span>
  )
  // 호스트(DOM) 엘리먼트 자식은 cloneElement 로 트리거 클래스/핸들러를 직접 주입(래퍼 없음 → 블록/flex 레이아웃 보존).
  // 컴포넌트 자식(OmwButton 등 비 forwardRef)은 ref 를 받을 수 없어 Radix Slot(asChild)가 경고 → inline-flex span 으로 래핑.
  const isHostElement = typeof triggerChild.type === 'string'
  const mergedTriggerClass = cx(
    (triggerChild.props as { className?: string }).className,
    triggerClass,
  )

  const onContextMenu = (e: ReactMouseEvent<HTMLElement>) => {
    e.preventDefault()
    setCtxPos({ x: e.clientX, y: e.clientY })
    setOpen(true)
  }

  // asChild(Trigger/Anchor)에 넘길 ref-able 트리거 노드. 호스트=자식 clone, 컴포넌트=span 래핑.
  // 래퍼 span 은 display 를 강제하지 않는다(기본 inline). inline-flex 는 원자 인라인 박스라,
  // 블록 컨텍스트(예: OmwList 의 ul)에서 자식(li 등)이 가로로 배치되는 문제를 유발한다. 기본 inline
  // 이면 내부 블록레벨 자식이 정상적으로 break-out 되어 세로 레이아웃이 보존되고, 인라인 트리거
  // (OmwButton 등)도 그대로 shrink-wrap 된다.
  const buildTrigger = (extra: Record<string, unknown>): ReactElement =>
    isHostElement ? (
      cloneElement(triggerChild as ReactElement<Record<string, unknown>>, {
        className: mergedTriggerClass,
        ...extra,
      })
    ) : (
      <span className={triggerClass} {...extra}>
        {children}
      </span>
    )

  const handleOpenChange = (next: boolean) => {
    if (!next) setActiveKey(null)
    setOpen(next)
  }
  // 다른 인스턴스가 레지스트리로 호출할 "자신 닫기" 는 최신 handleOpenChange 를 가리켜야 한다.
  // render 단계에서 ref 를 쓰면 React 19 concurrent(폐기되는 render)에서 계약 위반이므로 commit 후
  // 갱신한다. registry 는 passive effect 라, layout effect 로 먼저 갱신해 모든 registry 호출 시점에
  // ref 가 항상 최신임을 보장한다(두 드롭다운이 같은 commit 에 열리는 엣지 포함).
  useLayoutEffect(() => {
    closeSelfRef.current = () => handleOpenChange(false)
  })

  // 열릴 때: 조상이 아닌 열린 드롭다운을 모두 닫고(= 한 트리만 열림) 자신을 레지스트리에 등록한다.
  // controlled·uncontrolled 모두 isOpen 전이를 여기서 일괄 처리한다.
  useEffect(() => {
    if (!isOpen) return
    openDropdowns.forEach((entry, id) => {
      if (id === baseId || ancestorIds.includes(id)) return
      entry.close()
    })
    const entry = { close: () => closeSelfRef.current() }
    openDropdowns.set(baseId, entry)
    return () => {
      if (openDropdowns.get(baseId) === entry) openDropdowns.delete(baseId)
    }
  }, [isOpen, ancestorIds, baseId])

  const contentEl = (
    <PopoverPrimitive.Portal container={getPopupContainer?.(document.body)}>
      <PopoverPrimitive.Content
        ref={contentRef}
        role="menu"
        className={cx(
          'om-dropdown',
          `om-dropdown-placement-${placement}`,
          'om-dropdown-overlay',
          overlayClassName,
        )}
        side={side}
        align={rAlign}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        style={overlayStyle}
        onInteractOutside={(e) => {
          const el = contentRef.current
          if (el == null) return
          const orig = e.detail.originalEvent as Event
          if ('clientX' in orig && 'clientY' in orig) {
            // pointer: 스크롤바 gutter(콘텐츠 bounding box 안이지만 스크롤 영역)를 drag/click 하면 Radix 가
            //   이를 "외부" 로 오인해 닫는다 → 좌표가 콘텐츠 박스 안이면 닫힘을 막는다(스크롤바 drag 유지).
            const { clientX, clientY } = orig as MouseEvent
            const r = el.getBoundingClientRect()
            if (
              clientX >= r.left &&
              clientX <= r.right &&
              clientY >= r.top &&
              clientY <= r.bottom
            ) {
              e.preventDefault()
            }
            return
          }
          // 포커스 기반 dismiss(focusout — 스크롤바 grab 시 메뉴 UL blur·포커스 이동)로는 닫지 않는다.
          //   실제 닫힘은 콘텐츠 밖 pointer 클릭·Esc·항목 선택으로만.
          e.preventDefault()
        }}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          // 콤보박스(EmailSelect)는 menu.tabIndex 를 명시(-1)해 포커스를 트리거(contenteditable)에 유지 →
          //   메뉴 ul 로 포커스 이동 금지. tabIndex 미지정(일반 드롭다운)만 ul 포커스(키보드 로빙).
          if (menu?.tabIndex == null) menuUlRef.current?.focus()
        }}
      >
        {/* 팝업 안에 중첩된 OmwDropdownMenu 가 조상 체인(자신 포함)을 상속 → 공존 허용 판정. */}
        <DropdownAncestorContext.Provider value={childAncestorIds}>
          {content}
        </DropdownAncestorContext.Provider>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )

  if (mode === 'contextMenu') {
    return (
      <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Anchor asChild>
          <span
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: ctxPos.x,
              top: ctxPos.y,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        </PopoverPrimitive.Anchor>
        {buildTrigger({ onContextMenu })}
        {contentEl}
      </PopoverPrimitive.Root>
    )
  }

  if (mode === 'none') {
    return (
      <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Anchor asChild>{buildTrigger({})}</PopoverPrimitive.Anchor>
        {contentEl}
      </PopoverPrimitive.Root>
    )
  }

  // click(기본)
  return (
    <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      {/* disabled 는 setOpen 가드로 차단(트리거 자식이 비폼 엘리먼트일 수 있어 disabled attr 미전달). */}
      <PopoverPrimitive.Trigger asChild>{buildTrigger({})}</PopoverPrimitive.Trigger>
      {contentEl}
    </PopoverPrimitive.Root>
  )
}
